// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ActionableNotification,
  Button,
  Dropdown,
  InlineNotification,
  SkeletonPlaceholder,
} from '@carbon/react';
import { ChevronLeft, ChevronRight } from '@carbon/icons-react';
import { useDigitalTwin } from '../../../hooks/useDigitalTwin';
import { useConsoleAlarms } from '../../../hooks/useConsoleAlarms';
import { useActiveSite } from '../../../hooks/useActiveSite';
import { alarmsByAsset, alarmsFor } from '../../../utils/powerFlowAlarms';
import { useShellStore } from '../../../stores/shellStore';
import { useUrlState } from '../../../hooks/useUrlState';
import {
  ALARMS_BACKGROUND_POLL_INTERVAL_MS,
  TWIN_POLL_INTERVAL_MS,
} from '../../../config/queryConfig';
import { getEntityDisplayType } from '../../../utils/entityHelpers';
import { deriveAssetLiveState } from '../../../utils/assetLiveState';
import {
  deriveUnitFullLabel,
  deriveUnitShortLabel,
  upstreamIdOf,
} from '../../../utils/entityLabels';
import { buildPowerFlowModel, computePowerFlowStructureKey } from '../../../utils/powerFlowModel';
import { derivePowerFlowLive } from '../../../utils/powerFlowLive';
import { computePowerFlowKpis } from '../../../utils/powerFlowKpis';
import { deviceLayoutOptions } from './deviceLayoutOptions';
import { nameplateParts } from './nameplateParts';
import { KpiStrip } from './KpiStrip';
import { PowerFlowNotices } from './PowerFlowNotices';
import { SldLegend } from './SldLegend';
import { SldStage } from './SldStage';
import './PowerFlowPage.css';

/**
 * Site → Power Flow: the site's electrical distribution as a single-line
 * diagram, derived from the digital twin's power relationships.
 *
 * Replaces the type-grouped Asset Topology. The two used the same entities and
 * the same relationships; what changed is the axis. Asset Topology grouped by
 * `entity.type` and answered *"what kinds of equipment does this site have?"*.
 * This answers the operator's question — *"if this fails, what goes dark?"* —
 * by placing every asset at its derived electrical depth from the sources.
 *
 * Scope is deliberately narrow. This increment ships lines and connections:
 * real assets at real depth, real conductors, orthogonally routed, with the
 * main distribution bus drawn as a bar. No breakers, no fuses, no N.C./N.O., no
 * meter panels, no flow animation — each of those is a later PR gated on
 * upstream data that does not exist yet, and drawing them from nothing would be
 * decoration. See docs/power-flow/power-flow-single-line-diagram.md §4.
 *
 * Selection is held as an id in the shell store and the viewport lives in a ref
 * inside `useStageTransform`, so neither is data the 60-second telemetry poll
 * can clobber. See `powerFlowModel.ts` for the structural/live split that makes
 * the poll a re-render of unchanged geometry rather than a rebuild.
 */
const PowerFlowPage = () => {
  const { site_id: siteId } = useParams();
  const { entities, relationships, loading, error, truncated, refetch } = useDigitalTwin(siteId, {
    pollIntervalMs: TWIN_POLL_INTERVAL_MS,
  });

  const selectAsset = useShellStore(s => s.selectAsset);
  const clearSelectedAsset = useShellStore(s => s.clearSelectedAsset);
  const storedAssetId = useShellStore(s => s.selectedAssetId);
  const panelSiteId = useShellStore(s => s.selectedAssetSiteId);
  const selectedId = panelSiteId === siteId ? storedAssetId : null;

  const handleSelect = useCallback(
    (id: string | null) => {
      if (!id || !siteId) {
        clearSelectedAsset();
        return;
      }
      selectAsset(id, siteId);
    },
    [selectAsset, clearSelectedAsset, siteId]
  );

  /**
   * Load groups the reader has fanned out into individual chips.
   *
   * Structural rather than live state — expanding changes the geometry, so it
   * has to reach the layout — and deliberately *not* persisted: the collapsed
   * drawing is the one worth landing on, and a group expanded to answer one
   * question should not still be expanded a week later.
   */
  const [expandedGroups, setExpandedGroups] = useState<ReadonlySet<string>>(() => new Set());

  useEffect(() => setExpandedGroups(new Set()), [siteId]);

  const handleExpandGroup = useCallback((groupId: string) => {
    setExpandedGroups(current => new Set(current).add(groupId));
  }, []);

  const handleCollapseGroups = useCallback(() => setExpandedGroups(new Set()), []);

  /**
   * Whether the inferred tier is painted: standby runs, bypasses, the flow
   * overlay, block enclosures and the italic captions. Measured state stays
   * either way. Per the inference rules, a reader must be able to see the
   * drawing with every claim we made switched off.
   */
  const [showInferred, setShowInferred] = useState(true);
  const toggleInferred = useCallback(() => setShowInferred(current => !current), []);

  /**
   * Whether terminal loads are drawn as nodes. Off by default: a rack is a
   * count on the RPP that feeds it, and RPPs sharing a PDU are one `RPP × n`
   * badge — the mock's and the reference's convention, and the difference
   * between a drawing a reader can take in and one that is 46 boxes wide at
   * its deepest rank. Structural, so it reaches the layout.
   */
  const [showTerminalLoads, setShowTerminalLoads] = useState(false);
  const toggleTerminalLoads = useCallback(() => setShowTerminalLoads(current => !current), []);

  /**
   * Which page of blocks is drawn, picked from the dropdown at the canvas's
   * top-left and held in `?block=` so a refresh reopens the
   * same page. Structural — it decides which columns the sheet lays out. The
   * site is in the path, so another site is another URL and starts on page 0.
   * Garbage parses to 0 here and an out-of-range index is clamped by the sheet
   * build, so a stale link or a site with fewer blocks never draws an empty
   * canvas.
   */
  const [{ block }, patchUrl] = useUrlState({ block: '0' });
  const parsedBlock = Number.parseInt(block, 10);
  const sheetPage = Number.isFinite(parsedBlock) ? parsedBlock : 0;

  /**
   * The key and controls panel down the right edge. Collapsed by default so
   * the drawing gets the width; the stage refits on resize, so opening it
   * reframes the blocks rather than cropping them.
   */
  const [keyOpen, setKeyOpen] = useState(false);

  const entitiesById = useMemo(() => new Map(entities.map(e => [e.id, e])), [entities]);

  /**
   * The same site alarm set Home reads, through the same cache entry, polled
   * on the background alarm tier (ADR-0013) so a raised or closed alarm reaches
   * the badges within one interval instead of waiting for a reload (AB#36202).
   * The shell's driver (useAlarmsAutoRefresh) already polls the *active* site
   * at this tier, and every observer runs its own timer, so the page adds a
   * poll only when the route's site is a different one (nothing syncs
   * `:site_id` into the store).
   *
   * The page always scans on open, even over a warm cache, and says so until
   * that first scan lands; later polls refresh the marks silently. AEM is the
   * marks' only source now, so a failed scan with nothing cached is said, not
   * drawn as a clear site. While a site switch keeps the previous site's list
   * as placeholder, the marks stay dark: asset keys repeat across sites.
   */
  const { siteId: activeSiteId } = useActiveSite();
  const {
    data: siteAlarms,
    isFetchedAfterMount,
    isError: alarmsFailed,
    isPlaceholderData: alarmsOfOtherSite,
  } = useConsoleAlarms(siteId, {
    pollIntervalMs: siteId === activeSiteId ? undefined : ALARMS_BACKGROUND_POLL_INTERVAL_MS,
    refetchOnMount: 'always',
  });
  const alarmsNotice = !isFetchedAfterMount
    ? 'Scanning for alarms…'
    : alarmsFailed && !siteAlarms
      ? 'Alarms unavailable'
      : undefined;
  const entityIdOfUpstream = useMemo(
    () =>
      new Map(
        entities.flatMap(e => {
          const upstream = upstreamIdOf(e);
          return upstream ? [[upstream.toLowerCase(), e.id] as const] : [];
        })
      ),
    [entities]
  );
  const alarmsOf = useMemo(
    () => alarmsByAsset(alarmsOfOtherSite ? [] : (siteAlarms?.alarms ?? []), entityIdOfUpstream),
    [siteAlarms, alarmsOfOtherSite, entityIdOfUpstream]
  );

  const describeAsset = useCallback(
    (id: string) => {
      const entity = entitiesById.get(id);
      if (!entity) return { short: id, full: id, type: '', nameplate: undefined };
      return {
        short: deriveUnitShortLabel(entity),
        full: deriveUnitFullLabel(entity),
        type: getEntityDisplayType(entity.type),
        nameplate: nameplateParts(entity),
      };
    },
    [entitiesById]
  );

  /** What a load group's members are called, e.g. `IT Rack`. */
  const describeGroupType = useCallback(
    (entityType: string) => getEntityDisplayType(entityType) || 'assets',
    []
  );

  const structureKey = useMemo(
    () => computePowerFlowStructureKey(entities, relationships),
    [entities, relationships]
  );

  const expansionKey = useMemo(() => Array.from(expandedGroups).sort().join('|'), [expandedGroups]);

  const model = useMemo(
    () =>
      buildPowerFlowModel(entities, relationships, {
        expandedGroups,
        foldTerminalLoads: !showTerminalLoads,
        sheetPage,
        layout: deviceLayoutOptions({
          labelOf: id => describeAsset(id).short,
          groupLabelOf: describeGroupType,
        }),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [structureKey, expansionKey, showTerminalLoads, sheetPage]
  );

  const live = useMemo(
    () =>
      deriveAssetLiveState(
        entities,
        relationships,
        model.graph.vertices.map(vertex => vertex.id)
      ),
    [entities, relationships, model]
  );

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), TWIN_POLL_INTERVAL_MS);
    return () => clearInterval(tick);
  }, []);
  const powerLive = useMemo(
    () =>
      derivePowerFlowLive(entities, relationships, model.graph, model.inference, {
        now,
        staleAfterMs: 3 * TWIN_POLL_INTERVAL_MS,
      }),
    [entities, relationships, model, now]
  );

  const kpis = useMemo(
    () => computePowerFlowKpis(model.graph, model.inference, powerLive),
    [model, powerLive]
  );

  /**
   * Whether a count badge is actually on the canvas. `model.groups` also lists
   * groups that were folded into an RPP's sub-label and groups the reader has
   * expanded — neither leaves a badge to select, so keying one would send the
   * reader looking for it.
   */
  const hasGroupBadges = useMemo(
    () => model.groups.some(group => !group.folded && !group.expanded),
    [model]
  );

  /** Which live and inferred treatments the drawing carries, for the legend. */
  const legendPaint = useMemo(() => {
    const energizations = Array.from(powerLive.energizedOf.values());
    return {
      dead: energizations.includes('dead'),
      standby: energizations.includes('standby'),
      flowing: Array.from(powerLive.flowingOf.values()).some(Boolean),
      idle: model.conductors.some(
        conductor => !conductor.isTie && powerLive.flowingOf.get(conductor.id) !== true
      ),
      bypass: model.inference.bypassLinkIds.size > 0,
      blocks: model.inference.blocks.length > 0,
      stale: powerLive.staleReadingCount > 0,
      meters: powerLive.voltsOf.size > 0 || powerLive.ampsOf.size > 0,
      breakers: model.conductors.some(conductor => (conductor.via?.length ?? 0) > 0),
      breakerAlarm: model.conductors.some(
        conductor => alarmsFor(alarmsOf, conductor.via ?? []).length > 0
      ),
      inferredStates: powerLive.standbyIds.size > 0 || powerLive.exportKwOf.size > 0,
    };
  }, [model, powerLive, alarmsOf]);

  if (loading) {
    return (
      <div className="sld-page">
        <h1 className="sld-page__title">Power Flow</h1>
        <SkeletonPlaceholder className="sld-skeleton" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="sld-page">
        <h1 className="sld-page__title">Power Flow</h1>
        <ActionableNotification
          kind="error"
          lowContrast
          title="Could not load the site's power flow"
          subtitle={error}
          actionButtonLabel="Retry"
          onActionButtonClick={refetch}
          hideCloseButton
        />
      </div>
    );
  }

  return (
    <div className="sld-page">
      <h1 className="sld-page__title">Power Flow</h1>

      <KpiStrip kpis={kpis} />

      {!model.hasDiagram ? (
        <InlineNotification
          kind="info"
          lowContrast
          title="No power connections published for this site"
          subtitle={
            model.graph.diagnostics.unconnectedCount > 0
              ? `The digital twin has ${model.graph.diagnostics.unconnectedCount} assets for this site but no power relationships between them, so there is no chain to draw.`
              : 'The digital twin has no electrical assets for this site yet.'
          }
          hideCloseButton
        />
      ) : (
        <div className="sld-body">
          <div className="sld-canvas">
            {/*
              `role="application"` on the canvas suppresses screen-reader browse
              mode, and a drag-to-pan surface is unusable on a phone. The Asset
              Directory is the same data in a form that works in both cases, so
              the route to it sits with the block picker, always visible, rather
              than inside a panel that starts collapsed.
            */}
            <div className="sld-canvas__toolbar">
              {model.sheets.pages.length > 1 && (
                <Dropdown
                  id="power-flow-block"
                  className="sld-canvas__blocks"
                  titleText="Blocks"
                  hideLabel
                  label="Blocks"
                  size="sm"
                  items={model.sheets.pages}
                  itemToString={page => page?.title ?? ''}
                  selectedItem={
                    model.sheets.pages.find(page => page.index === model.sheets.pageIndex) ?? null
                  }
                  onChange={({ selectedItem }) => {
                    if (selectedItem) patchUrl({ block: String(selectedItem.index) });
                  }}
                />
              )}
              {expandedGroups.size > 0 && (
                <Button kind="ghost" size="sm" onClick={handleCollapseGroups}>
                  {`Collapse ${expandedGroups.size === 1 ? 'expanded loads' : `${expandedGroups.size} expanded groups`}`}
                </Button>
              )}
              <Link className="sld-toolbar__list-link" to={`/site/${siteId}/asset-directory`}>
                View as list
              </Link>
            </div>
            <SldStage
              /*
               * Remounted per tab, so the stage re-frames the blocks the reader
               * just switched to. `useStageTransform` deliberately stops
               * re-fitting once the operator has panned or zoomed — right for a
               * telemetry poll, wrong for a different drawing, which would
               * otherwise open under yesterday's pan at yesterday's zoom.
               */
              key={model.sheets.pageIndex}
              model={model}
              live={live}
              powerLive={powerLive}
              showInferred={showInferred}
              now={now}
              describeAsset={describeAsset}
              describeGroupType={describeGroupType}
              alarmsOf={alarmsOf}
              alarmsNotice={alarmsNotice}
              selectedId={selectedId}
              onSelect={handleSelect}
              onExpandGroup={handleExpandGroup}
              fitKey={`${structureKey}:${expansionKey}:${showTerminalLoads}:${model.sheets.pageIndex}`}
            />
          </div>
          <aside className="sld-key" aria-label="Key and controls">
            {/*
              The panel's left edge is the control: the whole strip, full
              height, so it is found by aiming at the border rather than at a
              small icon. Collapsed, it is all that shows of the panel.
            */}
            <button
              type="button"
              className="sld-key__edge"
              aria-label={keyOpen ? 'Hide key' : 'Show key'}
              title={keyOpen ? 'Hide key' : 'Show key'}
              aria-expanded={keyOpen}
              aria-controls="sld-key-body"
              onClick={() => setKeyOpen(open => !open)}
            >
              {keyOpen ? (
                <ChevronRight size={16} aria-hidden="true" />
              ) : (
                <ChevronLeft size={16} aria-hidden="true" />
              )}
            </button>
            {keyOpen && (
              <div id="sld-key-body" className="sld-key__body">
                <SldLegend
                  hasTies={model.diagnostics.tieCount > 0}
                  hasGroups={hasGroupBadges}
                  paint={legendPaint}
                  showInferred={showInferred}
                />
                <div className="sld-toolbar__controls">
                  <label className="sld-toolbar__toggle">
                    <input
                      type="checkbox"
                      checked={showInferred}
                      onChange={toggleInferred}
                      data-testid="power-flow-inferred-toggle"
                    />
                    Show inferred
                  </label>
                  <label
                    className="sld-toolbar__toggle"
                    title="Draw the racks as expandable group badges under the RPPs that feed them, instead of folding them into a count on each RPP"
                  >
                    <input
                      type="checkbox"
                      checked={showTerminalLoads}
                      onChange={toggleTerminalLoads}
                      data-testid="power-flow-terminal-loads-toggle"
                    />
                    Show rack groups
                  </label>
                </div>
              </div>
            )}
          </aside>
        </div>
      )}

      <PowerFlowNotices
        diagnostics={model.diagnostics}
        inference={model.inference}
        live={powerLive}
        now={now}
        nameOf={id => describeAsset(id).short}
        truncated={truncated}
      />
    </div>
  );
};

export default PowerFlowPage;
